import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { PROTECTION_STAGES } from '../data/experience';

export default function ProtectionJourney() {
  const sectionRef = useRef(null);
  const [active, setActive] = useState(0);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (reduceMotion || !window.matchMedia('(min-width: 1024px)').matches) return undefined;
    let ctx;
    let cancelled = false;
    Promise.all([import('gsap'), import('gsap/ScrollTrigger')]).then(([gsapModule, triggerModule]) => {
      if (cancelled) return;
      const gsap = gsapModule.gsap || gsapModule.default;
      const ScrollTrigger = triggerModule.ScrollTrigger;
      gsap.registerPlugin(ScrollTrigger);
      ctx = gsap.context(() => {
        const steps = gsap.utils.toArray('.protection-step');
        steps.forEach((step, index) => ScrollTrigger.create({
          trigger: step,
          start: 'top center',
          end: 'bottom center',
          onEnter: () => setActive(index),
          onEnterBack: () => setActive(index),
        }));
        ScrollTrigger.create({
          trigger: sectionRef.current,
          start: 'top top',
          end: 'bottom bottom',
          pin: '.protection-stage-visual',
          pinSpacing: false,
        });
      }, sectionRef);
    });
    return () => { cancelled = true; ctx?.revert(); };
  }, [reduceMotion]);

  const current = PROTECTION_STAGES[active];

  return (
    <section ref={sectionRef} className="protection-journey bg-charcoal text-white" data-testid="protection-journey">
      <div className="px-6 md:px-10 pt-20 lg:pt-28">
        <p className="eyebrow">01 / Protection system</p>
        <h2 className="font-display h-section mt-5 text-white max-w-[12ch]">Three layers. <span className="text-red">One car.</span></h2>
      </div>
      <div className="protection-layout px-6 md:px-10">
        <div className="protection-stage-visual" aria-live="polite">
          <AnimatePresence mode="wait">
            <motion.div key={current.id} initial={{ opacity: 0, scale: 1.025 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: reduceMotion ? 0 : .45 }} className="protection-visual-card">
              <img src={current.image} alt={`${current.title} protection at SupremeX`} />
              <div className="protection-visual-shade" />
              <span className="tag protection-visual-tag" style={{ background: current.color }}>{current.label}</span>
              <div className="protection-orbit" style={{ borderColor: current.color }} />
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="protection-copy">
          {PROTECTION_STAGES.map((stage, index) => (
            <article key={stage.id} className={`protection-step ${active === index ? 'is-active' : ''}`} tabIndex="0" onFocus={() => setActive(index)}>
              <div>
                <img className="protection-mobile-img" src={stage.image} alt="" />
                <p className="eyebrow" style={{ color: stage.color }}>{stage.n} / {stage.kicker}</p>
                <h3 className="font-display text-[clamp(64px,8vw,132px)] text-white mt-5">{stage.title}</h3>
                <p className="text-white/65 text-base md:text-xl leading-relaxed max-w-xl mt-6">{stage.body}</p>
                <a href={stage.id === 'impact' ? '/ppf' : `/services/${stage.id === 'surface' ? 'ceramic' : 'tint'}`} className="meta text-white inline-flex items-center gap-2 mt-8">Explore the layer <ArrowUpRight size={14} /></a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
